import { Expose, Type } from 'class-transformer';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject } from 'rxjs';
import { map, share, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, Optional } from '@angular/core';

import { SnOrganization, SnOrganizationSettings } from '@lib/api/entities';
import {
  FEATURE_FLAGS_KEY,
  LOCAL_ENABLED_INTEGRATIONS_KEY,
  LOCAL_FEATURE_FLAGS_KEY,
  LOCAL_LANGUAGE_OVERRIDE_KEY,
  STRING_SETTINGS_KEY,
} from '@lib/common/constants';
import { GlobalContextMap } from '@lib/data-dog-rum/global-context-map';
import { isPseudolocalizationAllowed, t, TranslationService } from '@lib/translation';

@Injectable({
  providedIn: 'root',
})
export class SnCurrentOrganization extends SnOrganization implements OnDestroy {
  @Expose()
  @Type(() => SnOrganizationSettings)
  public settings: SnOrganizationSettings;

  public alertsBoundary;
  public registryBoundary;

  private readonly _settings$ = new Subject<SnOrganizationSettings>();

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    @Optional() private readonly _translationService: TranslationService | null,
  ) {
    super();
  }

  get settings$(): Observable<SnOrganizationSettings> {
    return this._settings$.asObservable();
  }

  public ngOnDestroy(): void {
    this._settings$.complete();
  }

  public getProfile(): Observable<SnOrganization> {
    return this._httpClient.get('/api/organizations/current-organization/').pipe(
      tap((response: any) => {
        this.fromJSON(response);

        if (this.settings.isDemo) {
          this._overrideSettings();
          this._overrideIntegrations();
          this._overrideLanguage();
        }
        this._settings$.next(this.settings);
        this.getOrganizationAlarmBoundaries().subscribe();
      }),
      map((response: any) => new SnOrganization(response)),
      share(),
    );
  }

  public getSettings(): Observable<SnOrganizationSettings> {
    return this._httpClient.get(`/api/organizations/${this.id}/settings/`).pipe(
      map((response) => new SnOrganizationSettings(response)),
      tap((settings) => {
        this.settings = settings;
        if (this.settings.isDemo) {
          this._overrideSettings();
          this._overrideIntegrations();
          this._overrideLanguage();
        }
        this._settings$.next(this.settings);
      }),
      share(),
    );
  }

  public updateSettings(data: any): Observable<SnOrganizationSettings> {
    return this._httpClient.patch(`/api/organizations/${this.id}/settings/`, data).pipe(
      map((settings) => new SnOrganizationSettings(settings)),
      tap((settings) => {
        this.settings = settings;
        this._settings$.next(this.settings);
      }),
    );
  }

  public getLogData(): GlobalContextMap['organization'] {
    return {
      id: this.id,
      name: this.name,
      enabledIntegrations: this.enabledIntegrations,
      isExternallyManaged: this.isExternallyManaged,
      presenceApiEnableMode: this.settings?.presenceApiEnableMode,
    };
  }

  public getOrganizationAlarmBoundaries(): Observable<any> {
    return this._httpClient.get(`/api/organizations/${this.id}/settings/boundaries/`).pipe(
      tap((response) => {
        this.alertsBoundary = response?.alertsBoundary;
        if (this.settings) {
          this.settings.alertsBoundary = response.alertsBoundary;
        }
      }),
    );
  }

  public updateOrganizationAlarmBoundaries(data: any): Observable<any> {
    return this._httpClient.patch(`/api/organizations/${this.id}/settings/boundaries/`, data).pipe(
      tap({
        next: () => {
          this._toastrService.success(t('settings.dashboard.boundary.boundarySavedSuccessfully'));
        },
        error: () => {
          this._toastrService.error(t('settings.dashboard.boundary.unableToSaveBoundary'));
        },
      }),
    );
  }

  public getRegistryBoundary(): Observable<any> {
    return this._httpClient.get(`/api/registry/${this.id}/boundary/`).pipe(
      tap((response) => {
        this.registryBoundary = response?.boundary;
      }),
    );
  }

  public updateRegistryBoundary(data: any): Observable<any> {
    return this._httpClient.patch(`/api/registry/${this.id}/boundary/`, data).pipe(
      tap({
        next: () => {
          this._toastrService.success(t('settings.dashboard.boundary.boundaryUpdatedSuccessfully'));
        },
        error: () => {
          this._toastrService.error(t('settings.dashboard.boundary.unableToUpdateBoundary'));
        },
      }),
    );
  }

  private _overrideSettings(): void {
    // Override existing flags
    const toggledFeatureFlags = JSON.parse(localStorage.getItem(FEATURE_FLAGS_KEY));
    for (const flag in toggledFeatureFlags) {
      this.settings[flag] = toggledFeatureFlags[flag].value;
    }

    // Override string settings
    const stringSettings = JSON.parse(localStorage.getItem(STRING_SETTINGS_KEY));
    for (const setting in stringSettings) {
      this.settings[setting] = stringSettings[setting].value;
    }

    // Add local flags
    const localFlags = JSON.parse(localStorage.getItem(LOCAL_FEATURE_FLAGS_KEY));
    for (const flag in localFlags) {
      this.settings[flag] = localFlags[flag].value;
    }
  }

  private _overrideIntegrations(): void {
    // Get local enabled integrations from localStorage
    const localIntegrations = JSON.parse(localStorage.getItem(LOCAL_ENABLED_INTEGRATIONS_KEY));
    if (!localIntegrations) {
      return;
    }

    // Create a copy of the current enabled integrations to modify
    const updatedIntegrations = [...(this.enabledIntegrations || [])];

    // Process each local integration
    for (const integrationName in localIntegrations) {
      const integration = localIntegrations[integrationName];
      const currentIndex = updatedIntegrations.indexOf(integrationName);

      if (integration.enabled && currentIndex === -1) {
        // Add integration if enabled and not already present
        updatedIntegrations.push(integrationName);
      } else if (!integration.enabled && currentIndex > -1) {
        // Remove integration if disabled and currently present
        updatedIntegrations.splice(currentIndex, 1);
      }
    }

    // Update the enabled integrations array
    this.enabledIntegrations = updatedIntegrations;
  }

  private _overrideLanguage(): void {
    if (!this._translationService) {
      return;
    }
    
    const languageOverride = localStorage.getItem(LOCAL_LANGUAGE_OVERRIDE_KEY);
    if (languageOverride) {
      if (languageOverride === 'en-XA' && !isPseudolocalizationAllowed(this.settings.isDemo)) {
        console.warn('Attempted to use pseudolocalization (en-XA) in restricted environment. Ignoring override.');
        return;
      }
      this._translationService.setActiveLang(languageOverride);
    }
  }
}
